import { format } from "date-fns";
import { nb } from "date-fns/locale";

/**
 * Compress image to target size for optimal upload
 */
export const compressImage = async (
  file: File,
  maxWidthOrHeight: number = 1920,
  quality: number = 0.85
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidthOrHeight) {
            height *= maxWidthOrHeight / width;
            width = maxWidthOrHeight;
          }
        } else {
          if (height > maxWidthOrHeight) {
            width *= maxWidthOrHeight / height;
            height = maxWidthOrHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas to Blob failed"));
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          quality
        );
      };

      img.onerror = reject;
    };

    reader.onerror = reject;
  });
};

/**
 * Generate share text for social media
 */
export const generateShareText = (
  foodName: string,
  babyName?: string,
  date?: Date
): string => {
  const dateStr = date ? format(date, "dd.MM.yyyy", { locale: nb }) : "";
  const babyText = babyName ? `${babyName} prøvde ` : "";

  return `${babyText}${foodName}${dateStr ? ` den ${dateStr}` : ""}! 

#førstesmak #minførstesmak #babyledweaning #blw #norskbaby #fastføde #babymat #blwnorge #småbarnsmat`;
};

/**
 * Check if Web Share API is available
 */
export const canShare = (): boolean => {
  return typeof navigator !== "undefined" && !!navigator.share;
};

/**
 * Share image with Web Share API
 */
export const shareImage = async (
  imageUrl: string,
  text: string,
  fileName: string = "forstesmak.jpg"
): Promise<boolean> => {
  if (!canShare()) {
    return false;
  }

  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const file = new File([blob], fileName, { type: "image/jpeg" });

    await navigator.share({
      title: "Første smak",
      text: text,
      files: [file],
    });

    return true;
  } catch (error) {
    const err = error as Error;
    
    // User cancelled - not an error
    if (err.name === "AbortError") {
      return false;
    }
    
    // NotAllowedError = iframe blokkering eller manglende tillatelse
    // Returner false for å trigger fallback i stedet for å kaste error
    if (err.name === "NotAllowedError") {
      console.warn("Web Share API blocked (likely iframe). Using fallback.");
      return false;
    }
    
    // Andre feil
    console.error("Share failed:", error);
    return false;
  }
};

/**
 * Fallback share (copy + download) for browsers without Web Share API
 */
export const fallbackShare = async (
  imageUrl: string,
  text: string,
  fileName: string = "forstesmak.jpg"
): Promise<void> => {
  // Copy text to clipboard
  await navigator.clipboard.writeText(text);

  // Download image
  const link = document.createElement("a");
  link.href = imageUrl;
  link.download = fileName;
  link.click();
};
